import { useState, useEffect, useRef } from "react";
import "./App.css";
import Sidebar from "./components/Sidebar";
import { FiBook, FiPenTool, FiSearch, FiFileText } from "react-icons/fi";
import { FiMenu } from "react-icons/fi";
import logo from "./assets/Spark-Logo.png";
import ReactMarkdown from "react-markdown";
import { FiPaperclip } from "react-icons/fi";

function ChatApp({ user, onLogout }) {
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [isTyping, setIsTyping] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [image, setImage] = useState(null);
    const [imageName, setImageName] = useState(null);

    const chatEndRef = useRef(null);

    // const API_URL = "http://localhost:3001";
    const API_URL = "https://sda-chat-api-d3eeg6hcewf8gdde.ukwest-01.azurewebsites.net";

    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages, isTyping]);

    const sendMessage = async (overrideText = null) => {
        const text = overrideText || input;

        if (!text.trim() && !image) return;

        const newMessage = {
            role: "user",
            content: text,
            image: image
        };

        const updatedMessages = [...messages, newMessage];

        setMessages(updatedMessages);
        setInput("");
        setImage(null);
        setIsTyping(true);

        const res = await fetch(`${API_URL}/api/chat`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ messages: updatedMessages })
        });

        if (!res.ok) {
            const text = await res.text();
            console.error("Server returned error:", text);
            throw new Error("Server error");
        }

        const data = await res.json();

        setIsTyping(false);

        setMessages([
            ...updatedMessages,
            { role: "assistant", content: data.reply }
        ]);
    };

    const quickSend = (text) => {
        setInput(text);
        sendMessage(text);
    };

    const startNewChat = () => {
        setMessages([]);
        setInput("");
    };

    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setImageName(file.name);

        const reader = new FileReader();
        reader.onloadend = () => {
            setImage(reader.result);
        };

        reader.readAsDataURL(file);
    };

    const removeAttachment = () => {
        setImage(null);
        setImageName(null);
    };

    return (
        <div className={`app-layout ${menuOpen ? "menu-open" : ""}`}>

            <div className="mobile-header">
                <img src={logo} alt="Spark Design Academy" className="mobile-logo" />

                <button className="mobile-menu-btn" onClick={() => setMenuOpen(true)}>
                    <FiMenu />
                </button>
            </div>

            {/* Desktop sidebar */}
            <Sidebar
                user={user}
                onNewChat={startNewChat}
                onLogout={onLogout}
            />

            {/* Mobile flyout sidebar */}
            <div className="sidebar-flyout">
                <Sidebar
                    user={user}
                    onNewChat={() => {
                        startNewChat();
                        setMenuOpen(false);
                    }}
                    onLogout={() => {
                        onLogout();
                        setMenuOpen(false);
                    }}
                />
            </div>

            {/* Page overlay */}
            <div className="menu-overlay" onClick={() => setMenuOpen(false)} />

            <div className="main-content">
                {messages.length === 0 && (
                    <div className="hero">
                        <h1>Hey Spark!</h1>
                        <h2>What’s on your mind today?</h2>

                        <div className="hero-input">
                            <input
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && sendMessage()}
                                placeholder="Ask Spark something..."
                            />
                        </div>

                        <div className="hero-actions">

                            <button className="hero-chip" onClick={() => quickSend("Can you help me with my workbook?")}>
                                <FiBook className="chip-icon" />
                                Help with my Workbook
                            </button>

                            <button className="hero-chip" onClick={() => quickSend("Can you explain a kit design concept?")}>
                                <FiPenTool className="chip-icon" />
                                Explain Kit Design
                            </button>

                            <button className="hero-chip" onClick={() => quickSend("Help me research inspiration for my kit design.")}>
                                <FiSearch className="chip-icon" />
                                Research Inspiration
                            </button>

                            <button className="hero-chip" onClick={() => quickSend("I need guidance on my final project brief.")}>
                                <FiFileText className="chip-icon" />
                                Final Project Guidance
                            </button>
                        </div>
                    </div>
                )}

                {messages.length > 0 && (
                    <div className="chat-area">
                        <div className="chat-title">
                            Spark Design Academy Assistant
                        </div>

                        <div className="chat-window">
                            {messages.map((m, i) => (
                                <div key={i} className={`msg ${m.role}`}>

                                    {m.image && (
                                        <img
                                            src={m.image}
                                            alt="Uploaded kit"
                                            className="chat-image"
                                        />
                                    )}

                                    {m.content && (
                                        <ReactMarkdown>{m.content}</ReactMarkdown>
                                    )}

                                </div>
                            ))}
                            {isTyping && (
                                <div className="msg assistant typing">
                                    <span className="dot"></span>
                                    <span className="dot"></span>
                                    <span className="dot"></span>
                                </div>
                            )}

                            <div ref={chatEndRef} />
                        </div>

                        <div className="chat-input-area">
                            {image && (
                                <div className="attachment-preview">
                                    <FiPaperclip />
                                    <span className="attachment-name">{imageName}</span>

                                    <button
                                        className="remove-attachment"
                                        onClick={removeAttachment}
                                    >
                                        ✕
                                    </button>
                                </div>
                            )}

                            <div className="input-row">
                                <input
                                    type="file"
                                    accept="image/*"
                                    id="imageUpload"
                                    style={{ display: "none" }}
                                    onChange={handleImageUpload}
                                />

                                <input
                                    value={input}
                                    onChange={(e) => setInput(e.target.value)}
                                    onKeyDown={(e) => e.key === "Enter" && sendMessage()}
                                    placeholder="Ask about this kit..."
                                />

                                <button
                                    className="attach-btn"
                                    onClick={() => document.getElementById("imageUpload").click()}
                                >
                                    <FiPaperclip />
                                </button>

                                <button onClick={() => sendMessage()}>
                                    Send
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default ChatApp;